import { useCallback, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { Button } from "~/shadcn/ui/button"
import { Form } from "~/shadcn/ui/form"
import { AnalyticsSource } from "~/util/analyticsEvents"
import {
  activeFilterTypes,
  changedFilters,
  influencerFilterType,
} from "~/util/filterTracking"
import { trackEvent } from "~/util/tracking"
import { SearchValues, searchDefaultValues, searchResolver } from "../schema"
import { AllFiltersButton } from "./all-filters/AllFiltersButton"
import { AllFiltersModal } from "./all-filters/AllFiltersModal"
import { CategoryFilter } from "./main-filters/CategoryFilter"
import { MainFiltersCountryFilter } from "./main-filters/CountryFilter"
import { PlatformFilter } from "./main-filters/PlatformFilter"
import { SearchFilter } from "./main-filters/SearchFilter"

export const SearchBar = ({
  setSearchValues,
  initialValues,
  showAgencyFilter = false,
  source = "roster_database",
}: {
  setSearchValues: (values: SearchValues) => void
  initialValues?: SearchValues
  showAgencyFilter?: boolean
  source?: AnalyticsSource
}) => {
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const form = useForm<SearchValues>({
    resolver: searchResolver,
    defaultValues: searchDefaultValues,
  })
  // The form applies every dimension at once, so the analytics events are
  // derived by comparing each applied search with the previous one.
  const appliedValues = useRef<SearchValues>(searchDefaultValues)
  const allFiltersApplied = useRef(false)

  const applySearch = useCallback(
    (values: SearchValues) => {
      setFilterModalOpen(false)
      setSearchValues(values)

      for (const change of changedFilters(
        appliedValues.current,
        values,
        influencerFilterType
      )) {
        trackEvent("apply_filter", { ...change, source })
      }

      if (allFiltersApplied.current) {
        allFiltersApplied.current = false
        const active = activeFilterTypes(values, influencerFilterType)
        trackEvent("apply_all_filters", {
          filters_count: active.length,
          active_filters: active.join(", "),
        })
      }

      appliedValues.current = values
    },
    [setSearchValues, source]
  )

  const onClear = () => {
    form.reset(searchDefaultValues)
    applySearch({})
    trackEvent("clear_filters", { source })
  }

  const onSubmit = form.handleSubmit(applySearch, (error) => {
    console.error(error)
  })

  useEffect(() => {
    if (initialValues) {
      form.reset({ ...searchDefaultValues, ...initialValues })
      applySearch({ ...searchDefaultValues, ...initialValues })
    }
  }, [form, applySearch, initialValues])

  return (
    <>
      <div className="lg:hidden">Open on desktop to use filters</div>

      <Form {...form}>
        <div className="hidden lg:flex flex-grow flex-row justify-stretch gap-4 max-w-5xl">
          <div className="basis-2/5">
            <SearchFilter onChange={onSubmit} />
          </div>
          <div className="basis-1/5 hidden 2xl:block">
            <PlatformFilter onChange={onSubmit} />
          </div>
          <div className="basis-1/5 hidden xl:block">
            <CategoryFilter onChange={onSubmit} />
          </div>
          <div className="basis-1/5 hidden lg:block">
            <MainFiltersCountryFilter onChange={onSubmit} />
          </div>
          <AllFiltersButton
            onClick={() => {
              setFilterModalOpen(true)
              trackEvent("open_all_filters_modal", { source })
            }}
          />
          <Button
            type="button"
            variant="linkSecondary"
            size="none"
            onClick={onClear}
          >
            Clear all
          </Button>
        </div>
        <AllFiltersModal
          open={filterModalOpen}
          setOpen={setFilterModalOpen}
          onClear={onClear}
          onSubmit={() => {
            allFiltersApplied.current = true
            onSubmit()
          }}
          showAgencyFilter={showAgencyFilter}
        />
      </Form>
    </>
  )
}
